import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import API, { UPLOADS_URL } from '../api/axios';
import SEO from '../components/SEO';
import PageBanner from '../components/PageBanner';
import usePage from '../hooks/usePage';
import Reveal from '../components/ui/Reveal';
import Lightbox from '../components/ui/Lightbox';

const FILES = [1,2,3,4,5,6,7,8,9,11,12,13,14,15,16,17];
const BASE_PATH = `${process.env.PUBLIC_URL}/images/gallery`;

const GalleryPage = () => {
  const pageData = usePage('gallery');
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    let active = true;
    API.get('/galleries/public')
      .then(res => { if (active) setAlbums(res.data.data || []); })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const fallbackImages = useMemo(() =>
    FILES.map((n) => ({ img: `${BASE_PATH}/${n}.jpeg`, alt: `Gallery Image ${n}` })),
  []);

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const showAlbums = albums.length > 0;

  return (
    <>
      <SEO
        slug="gallery"
        title="Photo Gallery"
        description="Explore our photo gallery at St. Andrews World School, Siddharth Vihar."
      />

      <PageBanner
        title="Photo Gallery"
        subtitle="Explore our activities and celebrations"
        breadcrumbs={[{ label: 'Home', link: '/' }, { label: 'Gallery' }]}
        backgroundImage={pageData.bannerImage}
        fallback={`${process.env.PUBLIC_URL}/images/page-banners/gallery.jpg`}
        loading={pageData.loading}
      />

      <section className="section bg-light">
        <div className="container">
          <Reveal>
            <div className="text-center mb-10">
              <div className="kicker">Our Gallery</div>
              <h2 className="h-section text-2xl md:text-3xl font-bold mt-2">Moments at St. Andrews</h2>
              <div className="divider-line mx-auto" />
              <p className="text-body text-base mt-4 max-w-2xl mx-auto">
                Explore our activities and celebrations, organised event-wise for a closer look at life at St. Andrews.
              </p>
            </div>
          </Reveal>

          {loading ? (
            <div className="loading-screen"><i className="fas fa-spinner fa-spin"></i></div>
          ) : showAlbums ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {albums.map((album, idx) => (
                <Reveal key={album.id || idx} delayMs={idx * 60}>
                  <Link to={`/gallery/${album.slug}`} className="group block bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-500">
                    <div className="relative overflow-hidden">
                      <img
                        src={album.cover_image ? `${UPLOADS_URL}/${album.cover_image}` : `${BASE_PATH}/1.jpeg`}
                        alt={album.title}
                        className="w-full h-[20rem] object-cover transition-transform duration-700 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                      <div className="absolute bottom-0 left-0 right-0 p-5 flex items-end justify-between gap-4">
                        <h3 className="text-white text-lg font-semibold leading-tight line-clamp-2">{album.title}</h3>
                        <span className="inline-flex items-center gap-2 text-white/90 font-medium text-sm whitespace-nowrap group-hover:text-white group-hover:gap-3 transition-all">
                          View Album <i className="fas fa-arrow-right text-xs"></i>
                        </span>
                      </div>
                    </div>
                    {album.description && (
                      <div className="p-5">
                        <p className="text-body text-sm leading-relaxed line-clamp-2">{album.description.replace(/<[^>]*>/g, '').trim()}</p>
                      </div>
                    )}
                  </Link>
                </Reveal>
              ))}
            </div>
          ) : (
            <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4 [column-fill:_balance]">
              {fallbackImages.map((img, idx) => (
                <Reveal key={idx} delayMs={idx * 40} className="mb-4 break-inside-avoid">
                  <button
                    type="button"
                    onClick={() => openLightbox(idx)}
                    className="group w-full rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-white/60 bg-white"
                  >
                    <div className="relative">
                      <img
                        src={img.img}
                        alt={img.alt}
                        className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                        <div className="w-12 h-12 rounded-full bg-white/90 text-primary flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-4 group-hover:translate-y-0">
                          <i className="fas fa-expand text-sm" />
                        </div>
                      </div>
                    </div>
                  </button>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <Lightbox
        open={lightboxOpen}
        items={fallbackImages}
        initialIndex={lightboxIndex}
        onClose={() => setLightboxOpen(false)}
      />
    </>
  );
};

export default GalleryPage;
